import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

/**
 * Shared chrome for the public legal pages (/terminos, /privacidad). Mirrors the
 * dark theme of LandingPage/AuthPage (bg-[#0b0a10]) so the docs feel part of the
 * product. Fully public: no <Protected>, no API calls — static content only.
 */
export default function LegalShell({ title, updatedLabel, children }) {
  return (
    <div className="min-h-screen bg-[#0b0a10] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#08070d]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="legal-home-link">
            <img src="/logo.png" alt="The Trading Dock" className="w-9 h-9 rounded-xl ring-1 ring-white/10" />
            <div>
              <div className="text-sm font-black tracking-tight">
                THE TRADING DOCK<sup className="text-[8px] opacity-60 ml-0.5">®</sup>
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">finance · since 2026</div>
            </div>
          </Link>
          <Link
            to="/"
            className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]" data-testid="legal-title">
            {title}
          </h1>
          {updatedLabel && (
            <p className="mt-3 text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">{updatedLabel}</p>
          )}
          <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-slate-300">{children}</div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#08070d]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            © 2026 <span className="text-slate-300 font-bold">Pryma Wellness Group Inc.</span> · Delaware, USA · The Trading Dock® es una marca registrada.
          </div>
          <div className="flex items-center gap-3">
            <Link to="/terminos" className="hover:text-white transition" data-testid="legal-footer-terms">Términos</Link>
            <span>·</span>
            <Link to="/privacidad" className="hover:text-white transition" data-testid="legal-footer-privacy">Privacidad</Link>
            <span>·</span>
            <span className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-violet-400" /> Pagos por Stripe</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Section heading + body block used inside the legal docs. */
export function LegalSection({ id, heading, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={id ? `legal-section-${id}` : undefined}>
      <h2 className="text-lg font-bold text-white tracking-tight">{heading}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
